import React, { useState, useEffect, useContext, useRef } from "react";
import "./styles.scss";
import L from "leaflet";
import { FeatureGroup, Map, Marker, Popup, TileLayer, Circle } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { IoMdClose } from "react-icons/io";
import MainContext from "../../Contexts/MainContext";
import api from "../../Services/api";
import { MARCAR_PONTO } from "../../Services/Endpoints";
import { showToast, getTodayDate } from "../../Functions";
import { GetAllDelimitadorPontoByUser, GetAllTiposRegistro, GetAllFeriados } from "../../Services/ApiCalls";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// Ghost button styles config
const TIPOS_MAIN = [
    { key: 'Entrada' },
    { key: 'Início Intervalo' },
    { key: 'Término Intervalo' },
    { key: 'Saída' },
];
const TIPOS_EXTRA = [
    { key: 'Extra - Entrada' },
    { key: 'Extra - Início Intervalo' },
    { key: 'Extra - Término Intervalo' },
    { key: 'Extra - Saída' },
];

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png",
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-shadow.png",
});

interface ModalRegistrarPontoProps {
    onClose: () => void;
    onSuccess: () => void;
    pontos?: any[];
}

const ModalRegistrarPonto: React.FC<ModalRegistrarPontoProps> = ({ onClose, onSuccess, pontos }) => {
    const { token, userLocalization, currentLoggedUserId } = useContext(MainContext);

    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [selectedTimeStr, setSelectedTimeStr] = useState<string>(new Date().toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' }));
    const [selectedType, setSelectedType] = useState<string | number | null>(null);
    const [showExtraOptions, setShowExtraOptions] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [lat, setLat] = useState(0);
    const [long, setLong] = useState(0);

    const mapRef = useRef<any>(null);

    const { statusCodeAllDelimitadorPontosUser, dataAllDelimitadorPontosUser } = GetAllDelimitadorPontoByUser(currentLoggedUserId);
    const { dataAllTiposRegistro } = GetAllTiposRegistro(token);
    const { dataAllFeriados } = GetAllFeriados(token);

    const [feriadoHoje, setFeriadoHoje] = useState<any>(null);

    useEffect(() => {
        if (dataAllFeriados && Array.isArray(dataAllFeriados)) {
            const yyyy = selectedDate.getFullYear();
            const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const dd = String(selectedDate.getDate()).padStart(2, '0');
            const searchDate = `${yyyy}-${mm}-${dd}`;
            
            const found = dataAllFeriados.find((f: any) => f.data === searchDate);
            setFeriadoHoje(found || null);
        }
    }, [selectedDate, dataAllFeriados]);

    useEffect(() => {
        if (userLocalization && userLocalization.length > 0) {
            setLat(Number(userLocalization[0]));
            setLong(Number(userLocalization[1]));
        }
    }, [userLocalization]);

    useEffect(() => {
        if (pontos) {
            const todayStr = new Date().toLocaleDateString("pt-BR", { day: '2-digit', month: '2-digit', year: 'numeric' });
            const todayPontos = pontos.filter((p: any) => p.data === todayStr);
            const count = todayPontos.length;
            if (count === 0) setSelectedType('Entrada');
            else if (count === 1) setSelectedType('Início Intervalo');
            else if (count === 2) setSelectedType('Término Intervalo');
            else if (count >= 3) setSelectedType('Saída');
        } else {
            setSelectedType('Entrada');
        }
    }, [pontos]);

    const handleSave = async () => {
        if (!selectedType) {
            showToast("ERROR", "Selecione o tipo de ponto", {});
            return;
        }

        const dateFormatted = selectedDate.toLocaleDateString("pt-BR", { day: '2-digit', month: '2-digit', year: 'numeric' });
        const timeFormatted = selectedTimeStr;

        let finalTypeId = selectedType;
        if (typeof selectedType === 'string' && dataAllTiposRegistro && Array.isArray(dataAllTiposRegistro)) {
            const keyword = selectedType.split(' - ').pop()?.toLowerCase() || selectedType.toLowerCase();
            const found = dataAllTiposRegistro.find((t: any) => {
                const n = (t.nome || t.descricao || t.nomeDoRegistro || t.tipo || "").toLowerCase();
                return n.includes(keyword) || keyword.includes(n);
            });
            if (found) {
                finalTypeId = found.id;
            }
        }

        const isExtra = typeof selectedType === 'string' && selectedType.toLowerCase().includes('extra');
        const nowTime = new Date().toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' });
        const manualAdjustment = selectedTimeStr !== nowTime;

        const payload = {
            manual: true,
            colaboradorId: currentLoggedUserId,
            data: dateFormatted,
            horario: timeFormatted,
            localizacao: userLocalization ? userLocalization.toString() : "",
            tipoDoRegistroId: finalTypeId,
            tipo: selectedType,
            observacao: selectedType,
            is_extra: isExtra,
            manual_adjustment: manualAdjustment
        };

        setIsSaving(true);
        try {
            await api.post(MARCAR_PONTO, payload, { headers: { Authorization: token } });
            showToast("SUCCESS", `Ponto registrado com sucesso às ${timeFormatted}`, {});
            onSuccess();
        } catch (err) {
            showToast("ERROR", "Erro ao registrar ponto", {});
        } finally {
            setIsSaving(false);
        }
    };

    const getTypeColor = (name: string) => {
        const n = name.toLowerCase();
        if (n.includes("entrada")) return "#32CD32"; // green
        if (n.includes("pausa")) return "#FFD700"; // yellow
        if (n.includes("retorno")) return "#1E90FF"; // blue
        if (n.includes("saída") || n.includes("saida")) return "#FF0000"; // red
        return "#6a0dad"; // purple default
    };

    return (
        <div className="modal-ponto__overlay">
            <div className="modal-ponto__container">
                <div className="modal-ponto__header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                    <div style={{ flex: 1 }}></div>
                    <button onClick={onClose} className="btn-close"><IoMdClose size={24} /></button>
                </div>

                <div className="modal-ponto__body">
                    {feriadoHoje && (
                        <div style={{ backgroundColor: '#e0e7ff', color: '#3730a3', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 500, border: '1px solid #c7d2fe' }}>
                            🏖️ Este dia é um feriado cadastrado ({feriadoHoje.nome})
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '16px' }}>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Data</label>
                            <DatePicker
                                selected={selectedDate}
                                onChange={(date: Date) => setSelectedDate(date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                            />
                        </div>

                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Hora</label>
                            <input
                                type="time"
                                value={selectedTimeStr}
                                onChange={(e) => setSelectedTimeStr(e.target.value)}
                                className="form-control"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Tipo de ponto</label>

                        {/* Painel principal */}
                        {!showExtraOptions ? (
                            <div className="tipo-grid">
                                {TIPOS_MAIN.map(t => (
                                    <button
                                        key={t.key}
                                        type="button"
                                        className={`tipo-btn ${selectedType === t.key ? 'active' : ''}`}
                                        onClick={() => setSelectedType(t.key)}
                                    >
                                        {t.key}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className="tipo-btn extra-toggle"
                                    onClick={() => setShowExtraOptions(true)}
                                >
                                    Hora Extra
                                </button>
                            </div>
                        ) : (
                            <div className="tipo-grid">
                                <button
                                    type="button"
                                    className="tipo-btn back-btn"
                                    onClick={() => { setShowExtraOptions(false); setSelectedType(null); }}
                                >
                                    Voltar
                                </button>
                                {TIPOS_EXTRA.map(t => (
                                    <button
                                        key={t.key}
                                        type="button"
                                        className={`tipo-btn ${selectedType === t.key ? 'active' : ''}`}
                                        onClick={() => setSelectedType(t.key)}
                                    >
                                        {t.key.replace('Extra - ', '')}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Localização</label>
                        <div className="map-container">
                            {lat !== 0 && long !== 0 ? (
                                <Map center={[lat, long]} ref={mapRef} zoom={17} scrollWheelZoom={true}>
                                    <FeatureGroup>
                                        {dataAllDelimitadorPontosUser && Array.isArray(dataAllDelimitadorPontosUser) && dataAllDelimitadorPontosUser.map((crl: any, idx: number) => (
                                            crl.latLng && crl.latLng.length >= 2 ? <Circle key={idx} center={[crl.latLng[0], crl.latLng[1]]} radius={Number(crl.radius)} /> : null
                                        ))}
                                    </FeatureGroup>
                                    <TileLayer attribution="google" url="http://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" />
                                    <Marker position={[lat, long]}>
                                        <Popup>Sua localização</Popup>
                                    </Marker>
                                </Map>
                            ) : (
                                <div style={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white'}}>
                                    <div style={{fontSize: 14, marginBottom: 8, fontWeight: 600}}>Localização atual</div>
                                    <div style={{fontSize: 12, opacity: 0.8}}>Aguardando permissão ou sinal de GPS...</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="modal-ponto__footer">
                    <button className="bt-salvar" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? "Salvando..." : "Registrar Ponto"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModalRegistrarPonto;
